import React, { useMemo } from 'react';
import { DecimalNumberInput } from '../../ui';
import { MenuItem } from '@mui/material';
import {
  ListPageFilterContainer,
  ListPagePrimaryFilterRow,
  ListPageFilterCollapse,
  ListPageFilterTextField,
  ListPageQuadSecondaryFilterRow,
  LocationScopeControl,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../common/list-page';
import type { SalesByPieceFiltersState } from './types';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';

interface SalesByPieceFiltersProps {
  filters: SalesByPieceFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<SalesByPieceFiltersState>>;
  appliedFilters: SalesByPieceFiltersState;
  onClearAppliedField: (field: keyof SalesByPieceFiltersState) => void;
  showMoreFilters: boolean;
  onToggleMoreFilters: () => void;
  onSearch: () => void;
  onReset: () => void;
  locations: Array<{ id: number; name: string }>;
  isLoading?: boolean;
  isLocationLocked?: boolean;
  onLocationScopeChange?: (payload: { locationId: string; locationName: string }) => void;
}

const SalesByPieceFilters: React.FC<SalesByPieceFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onToggleMoreFilters,
  onSearch,
  onReset,
  locations,
  isLoading = false,
  isLocationLocked = false,
  onLocationScopeChange,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearch();
  };

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.productCodeUnique) {
      chips.push({
        key: 'productCodeUnique',
        label: `Pieza: ${applied.productCodeUnique}`,
        onRemove: () => onClearAppliedField('productCodeUnique'),
      });
    }
    if (applied.orderCode) {
      chips.push({
        key: 'orderCode',
        label: `${SALES_ORDER_NUMBER_LABEL}: ${applied.orderCode}`,
        onRemove: () => onClearAppliedField('orderCode'),
      });
    }
    if (applied.categoryName) {
      chips.push({
        key: 'categoryName',
        label: `Tipo: ${applied.categoryName}`,
        onRemove: () => onClearAppliedField('categoryName'),
      });
    }
    if (BRANCH_RECEIPT_NUMBERING_ENABLED && applied.branchReceiptCode) {
      chips.push({
        key: 'branchReceiptCode',
        label: `${BRANCH_RECEIPT_NUMBER_LABEL}: ${applied.branchReceiptCode}`,
        onRemove: () => onClearAppliedField('branchReceiptCode'),
      });
    }
    if (applied.sellerSearch) {
      chips.push({
        key: 'sellerSearch',
        label: `Vendedor: ${applied.sellerSearch}`,
        onRemove: () => onClearAppliedField('sellerSearch'),
      });
    }
    if (applied.hasCustomer === 'true') {
      chips.push({
        key: 'hasCustomer',
        label: 'Cliente: Con cliente',
        onRemove: () => onClearAppliedField('hasCustomer'),
      });
    } else if (applied.hasCustomer === 'false') {
      chips.push({
        key: 'hasCustomer',
        label: 'Cliente: Sin cliente',
        onRemove: () => onClearAppliedField('hasCustomer'),
      });
    }
    if (applied.saleDateFrom) {
      chips.push({
        key: 'saleDateFrom',
        label: `Desde: ${applied.saleDateFrom}`,
        onRemove: () => onClearAppliedField('saleDateFrom'),
      });
    }
    if (applied.saleDateTo) {
      chips.push({
        key: 'saleDateTo',
        label: `Hasta: ${applied.saleDateTo}`,
        onRemove: () => onClearAppliedField('saleDateTo'),
      });
    }
    if (applied.customerSearch) {
      chips.push({
        key: 'customerSearch',
        label: `Nombre cliente: ${applied.customerSearch}`,
        onRemove: () => onClearAppliedField('customerSearch'),
      });
    }
    if (applied.finalPriceMin) {
      chips.push({
        key: 'finalPriceMin',
        label: `Precio min: ${applied.finalPriceMin}`,
        onRemove: () => onClearAppliedField('finalPriceMin'),
      });
    }
    if (applied.finalPriceMax) {
      chips.push({
        key: 'finalPriceMax',
        label: `Precio max: ${applied.finalPriceMax}`,
        onRemove: () => onClearAppliedField('finalPriceMax'),
      });
    }
    if (applied.daysUntilSaleMin) {
      chips.push({
        key: 'daysUntilSaleMin',
        label: `Días min: ${applied.daysUntilSaleMin}`,
        onRemove: () => onClearAppliedField('daysUntilSaleMin'),
      });
    }
    if (applied.daysUntilSaleMax) {
      chips.push({
        key: 'daysUntilSaleMax',
        label: `Días max: ${applied.daysUntilSaleMax}`,
        onRemove: () => onClearAppliedField('daysUntilSaleMax'),
      });
    }

    return chips;
  }, [appliedFilters, onClearAppliedField]);

  return (
    <ListPageFilterContainer>
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={onToggleMoreFilters}
        onReset={onReset}
        onSearch={onSearch}
        betweenResetAndSearch={
          <LocationScopeControl
            locations={locations}
            value={filters.locationId}
            locked={isLocationLocked}
            disabled={isLoading}
            onChange={(payload) => {
              if (isLocationLocked) return;
              onLocationScopeChange?.(payload);
            }}
          />
        }
        searchFieldProps={{
          label: 'Código de pieza',
          name: 'productCodeUnique',
          value: filters.productCodeUnique,
          onChange: handleChange,
          onKeyDown: handleKeyDown,
          placeholder: 'Buscar por código',
          disabled: isLoading,
        }}
        secondarySearchFieldProps={{
          label: SALES_ORDER_NUMBER_LABEL,
          name: 'orderCode',
          value: filters.orderCode,
          onChange: handleChange,
          onKeyDown: handleKeyDown,
          placeholder: 'Buscar por número de venta',
          disabled: isLoading,
        }}
      />

      <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <ListPageQuadSecondaryFilterRow>
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Tipo de producto"
            name="categoryName"
            value={filters.categoryName}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          {BRANCH_RECEIPT_NUMBERING_ENABLED ? (
            <ListPageFilterTextField
              fullWidth
              variant="outlined"
              size="medium"
              label={BRANCH_RECEIPT_NUMBER_LABEL}
              name="branchReceiptCode"
              value={filters.branchReceiptCode}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              InputLabelProps={{ shrink: true }}
            />
          ) : null}
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Vendedor"
            name="sellerSearch"
            value={filters.sellerSearch}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Cliente"
            name="hasCustomer"
            value={filters.hasCustomer}
            onChange={handleChange}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="true">Con cliente</MenuItem>
            <MenuItem value="false">Sin cliente</MenuItem>
          </ListPageFilterTextField>
        </ListPageQuadSecondaryFilterRow>

        <ListPageQuadSecondaryFilterRow>
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha desde"
            name="saleDateFrom"
            type="date"
            value={filters.saleDateFrom}
            onChange={handleChange}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha hasta"
            name="saleDateTo"
            type="date"
            value={filters.saleDateTo}
            onChange={handleChange}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Nombre cliente"
            name="customerSearch"
            value={filters.customerSearch}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Precio mínimo"
            value={filters.finalPriceMin}
            onChange={(val) =>
              setFilters((prev) => ({
                ...prev,
                finalPriceMin: val != null ? String(val) : '',
              }))
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            decimalPlaces={2}
            min={0}
            InputLabelProps={{ shrink: true }}
          />
        </ListPageQuadSecondaryFilterRow>

        <ListPageQuadSecondaryFilterRow>
          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Precio máximo"
            value={filters.finalPriceMax}
            onChange={(val) =>
              setFilters((prev) => ({
                ...prev,
                finalPriceMax: val != null ? String(val) : '',
              }))
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            decimalPlaces={2}
            min={0}
            InputLabelProps={{ shrink: true }}
          />
          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Días mín. hasta venta"
            value={filters.daysUntilSaleMin}
            onChange={(val) =>
              setFilters((prev) => ({
                ...prev,
                daysUntilSaleMin: val != null ? String(val) : '',
              }))
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            decimalPlaces={0}
            min={0}
            InputLabelProps={{ shrink: true }}
          />
          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Días máx. hasta venta"
            value={filters.daysUntilSaleMax}
            onChange={(val) =>
              setFilters((prev) => ({
                ...prev,
                daysUntilSaleMax: val != null ? String(val) : '',
              }))
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            decimalPlaces={0}
            min={0}
            InputLabelProps={{ shrink: true }}
          />
        </ListPageQuadSecondaryFilterRow>
      </ListPageFilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </ListPageFilterContainer>
  );
};

export default SalesByPieceFilters;
